

export type HelpTopicId =
	| 'geral'
	| 'predicao'
	| 'mapeamento'
	| 'entrada-saida'
	| 'fazendas'
	| 'usuarios'
	| 'baias'
	| 'ortofotos';

export interface HelpSection {

	title: string;

	content: string;
}

export interface HelpTopic {
	id: HelpTopicId;

	title: string;

	menuPath: string;
	icon: string;

	summary: string;

	route?: string;

	adminOnly?: boolean;
	sections: HelpSection[];
}

export const DEFAULT_HELP_TOPIC: HelpTopicId = 'geral';

export const HELP_TOPICS: HelpTopic[] = [
	{
		id: 'geral',
		title: 'Visão geral do sistema',
		menuPath: 'Ajuda › Geral',
		icon: 'pi pi-compass',
		summary:
			'O sistema monitora o rebanho a partir das imagens enviadas de cada baia. Aqui estão as noções comuns a todas as telas: navegação pelo menu, uso das tabelas e o significado dos termos usados no restante da documentação.',
		sections: [
			{
				title: 'Como navegar entre as telas?',
				content: `<ol>
			<li>Use o <strong>menu lateral</strong> para abrir as telas. Ele é aberto pelo ícone de menu na barra superior quando a janela é estreita (celular/tablet).</li>
			<li>Os itens são agrupados por assunto: <strong>Serviços</strong> (Predições, Mapeamento, Métricas), <strong>Configurações</strong> (Entrada/saída) e <strong>ConfigADM</strong> (Fazendas, Usuários, Baias, Ortofotos — apenas para administradores).</li>
			<li>Em <strong>Ajuda</strong> você encontra esta documentação, com um tópico para cada tela.</li>
			<li><strong>Sair</strong> encerra a sessão e devolve você à tela de login.</li>
		</ol>`,
			},
			{
				title: 'O que significam os termos usados no sistema?',
				content: `<ul>
			<li><strong>Fazenda</strong>: a propriedade monitorada. Seu usuário enxerga apenas os dados da fazenda à qual está vinculado.</li>
			<li><strong>Baia</strong>: cada piquete/curral da fazenda. É a unidade em que as imagens são organizadas e os resultados são apresentados.</li>
			<li><strong>Predição</strong>: o processamento das imagens de um levantamento, feito no servidor, que classifica os animais.</li>
			<li><strong>Atípico</strong>: animal que a predição apontou fora da condição normal — é o que deve ser conferido em campo.</li>
			<li><strong>Levantamento</strong>: a data e a hora em que as imagens foram capturadas (diferente da data do processamento).</li>
			<li><strong>Ortofoto</strong>: a imagem aérea da fazenda usada como fundo do mapa na tela de Mapeamento.</li>
		</ul>`,
			},
			{
				title: 'Como usar as tabelas (buscar, filtrar e ordenar)?',
				content: `<ol>
			<li>Use o campo <strong>"Buscar..."</strong>, no canto superior direito da tabela, para procurar em todas as colunas ao mesmo tempo.</li>
			<li>Clique no <strong>título de uma coluna</strong> para ordenar por ela; clique novamente para inverter a ordem.</li>
			<li>Clique no ícone de <strong>funil</strong> ao lado do título para filtrar apenas por aquela coluna.</li>
			<li>Use <strong>"Limpar filtros"</strong> para voltar à listagem completa.</li>
			<li>O ícone de <strong>atualizar</strong> (seta circular) recarrega os dados sem precisar recarregar a página.</li>
			<li>Quando houver muitos registros, use a <strong>paginação</strong> no rodapé para avançar entre as páginas.</li>
			<li>OBS: A ordenação e os filtros escolhidos ficam salvos no navegador e são reaplicados quando você volta à tela.</li>
		</ol>`,
			},
			{
				title: 'Não encontro uma tela no menu. O que fazer?',
				content: `<ol>
			<li>O menu mostra apenas as telas liberadas para o seu perfil: as telas de <strong>ConfigADM</strong> (Fazendas, Usuários, Baias e Ortofotos) aparecem somente para administradores.</li>
			<li>A tela de <strong>Métricas</strong> exige um usuário vinculado a uma fazenda. Sem esse vínculo, o sistema avisa que você não possui acesso às métricas.</li>
			<li>Se você deveria ter acesso e o item não aparece, peça ao administrador para revisar o seu cadastro em <strong>Usuários</strong>.</li>
		</ol>`,
			},
		],
	},
	{
		id: 'predicao',
		title: 'Predições',
		menuPath: 'Serviços › Predições',
		icon: 'pi pi-check-square',
		route: '/main/results',
		summary:
			'Na tela de Predições você envia as imagens de um levantamento para processamento e acompanha o histórico dos envios. É também por ela que se abrem os resultados: as fotos dos animais atípicos, baia por baia.',
		sections: [
			{
				title: 'Como enviar as imagens e gerar uma nova predição?',
				content: `<ol>
			<li>No menu lateral, abra <strong>Serviços › Predições</strong>.</li>
			<li>Clique em <strong>"Nova Predição"</strong>, acima da tabela.</li>
			<li>Informe a <strong>Data do levantamento</strong> e a <strong>Hora (00-23)</strong> em que as imagens foram capturadas.</li>
			<li>Clique em <strong>"Carregar pasta e gerar predição"</strong> e selecione a pasta com as imagens.</li>
			<li>O envio começa assim que a pasta é confirmada e a janela se fecha sozinha.</li>
			<li>OBS: A pasta enviada deve conter <strong>uma subpasta por baia</strong>, e as imagens dentro de cada subpasta.</li>
			<li>OBS: O botão só é liberado depois que a data e a hora forem preenchidas — sem elas o sistema avisa "Insira uma data e hora de levantamento válidas".</li>
		</ol>`,
			},
			{
				title: 'Como acompanhar um envio em andamento?',
				content: `<ol>
			<li>Cada envio vira uma linha na tabela de Predições, com o <strong>Responsável</strong>, a data do <strong>Processamento</strong> e a data do <strong>Levantamento</strong>.</li>
			<li>Enquanto o servidor processa, a linha fica marcada como em processamento; ao terminar, ela passa a exibir os totais.</li>
			<li>Use o botão de <strong>atualizar</strong> da tabela para verificar se o processamento já terminou.</li>
			<li>OBS: O processamento é feito no servidor e pode levar alguns minutos — você pode sair da tela e voltar depois.</li>
		</ol>`,
			},
			{
				title: 'O que cada coluna da tabela mostra?',
				content: `<ul>
			<li><strong>Responsável</strong>: usuário que enviou as imagens.</li>
			<li><strong>Processamento</strong>: quando o envio foi processado pelo sistema.</li>
			<li><strong>Levantamento</strong>: data e hora informadas no momento do envio. Registros antigos, anteriores a esse campo, aparecem como <strong>----</strong>.</li>
			<li><strong>Imagens4K</strong>: total de imagens enviadas naquele levantamento.</li>
			<li><strong>Baias</strong>: quantas baias foram processadas.</li>
			<li><strong>Atipicos</strong>: quantos animais fora da condição normal foram encontrados.</li>
			<li><strong>Tempo</strong>: duração total do processamento.</li>
		</ul>`,
			},
			{
				title: 'Como ver os resultados de um envio?',
				content: `<ol>
			<li>Na tabela, localize a linha do levantamento desejado.</li>
			<li>Clique no ícone de <strong>olho ("Ver Resultado")</strong> na primeira coluna da linha.</li>
			<li>Na tela de resultados, escolha a <strong>baia</strong> pelo menu superior para carregar as imagens dela.</li>
			<li>O painel <strong>"Animais Atípicos"</strong> exibe as fotos dos animais apontados pela predição naquela baia.</li>
			<li>Clique em uma foto para abri-la em tela cheia e use as <strong>setas laterais</strong> para percorrer as demais.</li>
			<li>Use a <strong>paginação</strong> abaixo das fotos para ver mais imagens, e o botão <strong>"Voltar"</strong> para retornar à lista de predições.</li>
			<li>OBS: Quando a baia não tem ocorrências, aparece "Nenhum animal atípico encontrado para esta baia".</li>
		</ol>`,
			},
			{
				title: 'Como gerar o relatório em PDF de uma linha de baias?',
				content: `<ol>
			<li>Abra o resultado de um levantamento e selecione uma <strong>baia</strong>.</li>

			<li>O relatório reúne as imagens dos animais atípicos de toda a linha correspondente à baia selecionada.</li>
			<li>OBS: O botão só aparece quando a baia selecionada possui imagens.</li>
		</ol>`,
			},
		],
	},
	{
		id: 'mapeamento',
		title: 'Mapeamento',
		menuPath: 'Serviços › Mapeamento',
		icon: 'pi pi-map',
		route: '/main/statusFarm',
		summary:
			'O Mapeamento mostra a fazenda vista de cima, sobre a ortofoto, com um marcador por baia colorido conforme a condição do rebanho. É a visão rápida de onde estão os problemas — e por ela também se registram as entradas e saídas de animais.',
		sections: [
			{
				title: 'Como ler as cores dos marcadores no mapa?',
				content: `<ol>
			<li>No menu lateral, abra <strong>Serviços › Mapeamento</strong>.</li>
			<li>Clique em <strong>"Legenda"</strong>, no topo da tela, para consultar o significado das cores a qualquer momento:</li>
		</ol>
		<ul>
			<li><strong>Verde</strong>: mais de 75% dos animais em condição normal.</li>
			<li><strong>Amarelo</strong>: entre 50% e 75% dos animais em condição normal.</li>
			<li><strong>Vermelho</strong>: menos de 50% dos animais em condição normal — priorize a conferência dessas baias.</li>
			<li><strong>Branco</strong>: sem registro de animais.</li>
			<li><strong>Marrom</strong>: sem registro de entrada/saída — cadastre o período para que a baia passe a ser avaliada.</li>
		</ul>`,
			},
			{
				title: 'Como navegar pelo mapa?',
				content: `<ol>
			<li>Arraste o mapa para se deslocar pela fazenda e use a rolagem (ou os botões de zoom) para aproximar e afastar.</li>
			<li>Clique em um <strong>marcador de baia</strong> para abrir as informações dela.</li>
			<li>Se aparecer <strong>"Orthophoto pendente"</strong>, a imagem aérea da fazenda ainda não foi cadastrada — fale com o administrador (ver o tópico de Ortofotos).</li>
			<li>OBS: "Carregando ortofoto..." indica que a imagem está sendo baixada; aguarde alguns instantes.</li>
		</ol>`,
			},
			{
				title: 'Como cadastrar uma entrada de animais pelo mapa?',
				content: `<ol>
			<li>Clique no <strong>marcador da baia</strong> desejada e escolha <strong>"Cadastrar entrada"</strong>.</li>
			<li>Na janela que abrir, informe a <strong>Data de entrada</strong> e a <strong>Quantidade de entrada</strong>.</li>
			<li>Clique em <strong>"Cadastrar entrada"</strong> para salvar. O novo período aparece na <strong>Lista de períodos</strong>, na mesma janela.</li>
			<li>OBS: O botão só é liberado depois que a data e a quantidade forem preenchidas.</li>
		</ol>`,
			},
			{
				title: 'Como registrar a saída ou corrigir um período pelo mapa?',
				content: `<ol>
			<li>Na janela da baia, localize o período na <strong>Lista de períodos</strong>.</li>
			<li>Para encerrar o ciclo, clique em <strong>"Cadastrar saída"</strong>, informe a data e a quantidade de saída e confirme em <strong>"Confirmar saída"</strong>.</li>
			<li>Para corrigir dados já lançados, clique em <strong>"Editar"</strong>, ajuste os campos e clique em <strong>"Salvar"</strong>.</li>
			<li>OBS: O botão "Cadastrar saída" só aparece em períodos que ainda estão abertos (sem data de saída).</li>
			<li>OBS: A mesma rotina está disponível na tela <strong>Configurações › Entrada/saída</strong>, em formato de lista.</li>
		</ol>`,
			},
		],
	},
	{
		id: 'entrada-saida',
		title: 'Entrada e saída',
		menuPath: 'Configurações › Entrada/saída',
		icon: 'pi pi-calendar',
		route: '/main/entrada-saida',
		summary:
			'Nesta tela você registra os ciclos de ocupação de cada baia: quando os animais entraram, quantos entraram e quando saíram. Esse registro é o que permite ao sistema avaliar a condição do rebanho por baia no Mapeamento.',
		sections: [
			{
				title: 'Como cadastrar uma entrada de animais?',
				content: `<ol>
			<li>No menu lateral, abra <strong>Configurações › Entrada/saída</strong>.</li>
			<li>Na tabela, localize a baia desejada e abra a janela de períodos dela pelas ações da linha.</li>
			<li>Informe a <strong>Data de entrada</strong> e a <strong>Quantidade de entrada</strong>.</li>
			<li>Clique em <strong>"Cadastrar entrada"</strong>. O período passa a constar na <strong>Lista de períodos</strong>.</li>
			<li>OBS: O botão permanece desabilitado até que a data e a quantidade sejam informadas.</li>
		</ol>`,
			},
			{
				title: 'Como registrar a saída dos animais?',
				content: `<ol>
			<li>Abra a janela de períodos da baia e localize o período em aberto (sem data de saída).</li>
			<li>Clique em <strong>"Cadastrar saída"</strong>.</li>
			<li>Informe a <strong>Data de saída</strong> e a <strong>Quantidade de saída</strong>.</li>
			<li>Clique em <strong>"Confirmar saída"</strong> para encerrar o ciclo.</li>
			<li>OBS: Use o botão de cancelar (×) ao lado se quiser desistir sem salvar.</li>
		</ol>`,
			},
			{
				title: 'Como corrigir um período já cadastrado?',
				content: `<ol>
			<li>Na <strong>Lista de períodos</strong>, clique em <strong>"Editar"</strong> no período que deseja corrigir.</li>
			<li>Ajuste a data e a quantidade de entrada e, se o período já estiver encerrado, também a data e a quantidade de saída.</li>
			<li>Clique em <strong>"Salvar"</strong> para confirmar ou em <strong>"Cancelar"</strong> para descartar as alterações.</li>
			<li>OBS: Em um período já encerrado, os campos de saída passam a ser obrigatórios para salvar.</li>
		</ol>`,
			},
			{
				title: 'Por que manter esse registro em dia?',
				content: `<ol>
			<li>Sem período cadastrado, a baia aparece <strong>marrom</strong> no Mapeamento ("sem registro de entrada/saída") e não é avaliada.</li>
			<li>A quantidade informada é a referência para calcular o percentual de animais em condição normal exibido no mapa.</li>
			<li>OBS: Os mesmos cadastros podem ser feitos clicando na baia pelo <strong>Mapeamento</strong>.</li>
		</ol>`,
			},
		],
	},
	{
		id: 'fazendas',
		title: 'Fazendas',
		menuPath: 'ConfigADM › Fazendas',
		icon: 'pi pi-briefcase',
		route: '/main/farm',
		adminOnly: true,
		summary:
			'Tela administrativa de cadastro das fazendas monitoradas. A fazenda é a base de todo o resto: usuários, baias e ortofotos são sempre vinculados a uma delas.',
		sections: [
			{
				title: 'Como cadastrar uma nova fazenda?',
				content: `<ol>
			<li>No menu lateral, abra <strong>ConfigADM › Fazendas</strong>.</li>
			<li>Clique em <strong>"Cadastrar Nova Fazenda"</strong>.</li>
			<li>Preencha os dados da fazenda no formulário.</li>
			<li>Clique em <strong>"Enviar"</strong> para concluir o cadastro.</li>
		</ol>`,
			},
			{
				title: 'Como editar ou excluir uma fazenda cadastrada?',
				content: `<ol>
			<li>Localize a fazenda na tabela — use o campo <strong>"Buscar..."</strong> se a lista for longa.</li>
			<li>Clique no ícone de <strong>edição</strong> para atualizar os dados.</li>
			<li>Clique no ícone de <strong>exclusão</strong> para remover a fazenda do sistema e confirme a operação.</li>
			<li>OBS: Excluir uma fazenda afeta os usuários e as baias vinculados a ela — confira essas telas antes.</li>
		</ol>`,
			},
		],
	},
	{
		id: 'usuarios',
		title: 'Usuários',
		menuPath: 'ConfigADM › Usuários',
		icon: 'pi pi-users',
		route: '/main/customer',
		adminOnly: true,
		summary:
			'Tela administrativa de cadastro dos usuários do sistema. É aqui que se define quem acessa, com qual perfil e a qual fazenda a pessoa está vinculada.',
		sections: [
			{
				title: 'Como cadastrar um novo usuário?',
				content: `<ol>
			<li>No menu lateral, abra <strong>ConfigADM › Usuários</strong>.</li>
			<li>Clique em <strong>"Cadastrar Novo Usuário"</strong>.</li>
			<li>Preencha o formulário com os dados da pessoa, o perfil de acesso e a fazenda vinculada.</li>
			<li>Clique em <strong>"Enviar"</strong> para concluir o cadastro.</li>
			<li>OBS: O vínculo com uma fazenda é o que libera a tela de <strong>Métricas</strong> para o usuário.</li>
		</ol>`,
			},
			{
				title: 'Como editar ou excluir um usuário cadastrado?',
				content: `<ol>
			<li>Localize o usuário na tabela — use o campo <strong>"Buscar..."</strong> se a lista for longa.</li>
			<li>Clique no ícone de <strong>edição</strong> para alterar os dados ou o perfil de acesso.</li>
			<li>Clique no ícone de <strong>exclusão</strong> para remover o usuário do sistema e confirme a operação.</li>
			<li>OBS: Usuários do tipo <strong>Dev</strong> não podem ser removidos.</li>
		</ol>`,
			},
		],
	},
	{
		id: 'baias',
		title: 'Baias',
		menuPath: 'ConfigADM › Baias',
		icon: 'pi pi-map-marker',
		route: '/main/feedlot',
		adminOnly: true,
		summary:
			'Tela administrativa de cadastro das baias de cada fazenda. As baias definem como as imagens são organizadas nas predições e quais marcadores aparecem no Mapeamento.',
		sections: [
			{
				title: 'Como cadastrar uma nova baia?',
				content: `<ol>
			<li>No menu lateral, abra <strong>ConfigADM › Baias</strong>.</li>
			<li>Clique em <strong>"Cadastrar Nova Baia"</strong>.</li>
			<li>Selecione a <strong>fazenda</strong> à qual a baia pertence e preencha os demais dados do formulário.</li>
			<li>Clique em <strong>"Enviar"</strong> para concluir o cadastro.</li>
			<li>OBS: O nome cadastrado aqui deve corresponder ao nome da subpasta usada no envio de imagens em <strong>Predições</strong>.</li>
		</ol>`,
			},
			{
				title: 'Como editar ou excluir uma baia cadastrada?',
				content: `<ol>
			<li>Localize a baia na tabela — use o campo <strong>"Buscar..."</strong> se a lista for longa.</li>
			<li>Clique no ícone de <strong>edição</strong> para atualizar os dados.</li>
			<li>Clique no ícone de <strong>exclusão</strong> para remover a baia do sistema e confirme a operação.</li>
			<li>OBS: A remoção afeta os períodos de entrada/saída e o marcador da baia no Mapeamento.</li>
		</ol>`,
			},
		],
	},
	{
		id: 'ortofotos',
		title: 'Ortofotos',
		menuPath: 'ConfigADM › Ortofotos',
		icon: 'pi pi-image',
		route: '/main/ortofoto',
		adminOnly: true,
		summary:
			'Tela administrativa onde se envia a imagem aérea da fazenda. É essa imagem que serve de fundo para os marcadores das baias na tela de Mapeamento.',
		sections: [
			{
				title: 'Como cadastrar a ortofoto de uma fazenda?',
				content: `<ol>
			<li>No menu lateral, abra <strong>ConfigADM › Ortofotos</strong>.</li>
			<li>Clique em <strong>"Cadastrar Ortofoto"</strong>.</li>
			<li>Selecione a <strong>fazenda</strong> correspondente.</li>
			<li>Arraste a <strong>pasta da pirâmide de tiles</strong> para a área indicada, ou clique nela para selecionar a pasta.</li>
			<li>Confirme o envio e aguarde a conclusão do upload.</li>
			<li>OBS: É preciso selecionar a <strong>pasta</strong> inteira (tiles dos zooms 14 a 20) — arrastar arquivos soltos não funciona.</li>
		</ol>`,
			},
			{
				title: 'Enviei a ortofoto, mas o mapa continua vazio. O que verificar?',
				content: `<ol>
			<li>Confira se a ortofoto foi enviada para a <strong>fazenda correta</strong> na tabela desta tela.</li>
			<li>Abra o <strong>Mapeamento</strong>: se aparecer "Carregando ortofoto...", aguarde a imagem terminar de carregar.</li>
			<li>Se aparecer <strong>"Orthophoto pendente"</strong>, o processamento da imagem ainda não terminou ou a pasta enviada está incompleta — repita o envio com a pirâmide completa.</li>
		</ol>`,
			},
		],
	},
];

export function getHelpTopic(id: string | null | undefined): HelpTopic {
	return (
		HELP_TOPICS.find((topic) => topic.id === id) ??
		HELP_TOPICS.find((topic) => topic.id === DEFAULT_HELP_TOPIC)!
	);
}

export function getVisibleHelpTopics(role: string | null): HelpTopic[] {
	const isAdmin = role === 'admin' || role === 'adm' || role === 'dev';
	return HELP_TOPICS.filter((topic) => !topic.adminOnly || isAdmin);
}
